import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

/* ── Constants ─────────────────────────────────────────────────── */
const TYPES     = ['corrective','preventive','both'];
const SEVERITIES = ['critical','major','minor'];
const STATUSES  = ['open','in_progress','closed','cancelled'];

const typeLabels = { corrective: 'Corrective', preventive: 'Preventive', both: 'Both' };
const severityColors = { critical: '#ef4444', major: '#f59e0b', minor: '#eab308' };
const severityBg    = { critical: '#fef2f2', major: '#fffbeb', minor: '#fefce8' };
const statusColors  = { open: 'info', in_progress: 'warning', closed: 'success', cancelled: 'neutral' };
const statusLabels  = { open: 'Open', in_progress: 'In Progress', closed: 'Closed', cancelled: 'Cancelled' };

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const isPriv = (role) => role === 'admin' || role === 'manager';
const isOverdue = (dueDate, status) => dueDate && !['closed','cancelled'].includes(status) && new Date(dueDate) < new Date();

const emptyForm = () => ({
  title: '', description: '', type: 'corrective', source: '', severity: 'major', status: 'open',
  root_cause: '', immediate_action: '', related_batch: '', assigned_to: '', due_date: '',
});

const emptyActionForm = () => ({ description: '', assigned_to: '', due_date: '' });

/* ── Badge ──────────────────────────────────────────────────────── */
const Badge = ({ color, children }) => (
  <span className={`badge badge-${color}`} style={{ textTransform: 'capitalize', fontSize: '0.72rem' }}>{children}</span>
);

const SeverityBadge = ({ severity }) => (
  <span style={{
    display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
    color: severityColors[severity] || '#374151',
    background: severityBg[severity] || '#f3f4f6',
    border: `1px solid ${severityColors[severity] || '#d1d5db'}30`,
    textTransform: 'capitalize',
  }}>{severity}</span>
);

export default function CAPA() {
  const { user } = useAuth();

  /* ── List state ─────────────────────────────────────────────── */
  const [data,    setData]    = useState({ data: [], total: 0, page: 1, limit: 25 });
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);

  /* ── Filters ────────────────────────────────────────────────── */
  const [search,    setSearch]    = useState('');
  const [fStatus,   setFStatus]   = useState('');
  const [fSeverity, setFSeverity] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  /* ── Add/Edit modal ─────────────────────────────────────────── */
  const [modal,   setModal]   = useState({ open: false, mode: 'add', editId: null });
  const [form,    setForm]    = useState(emptyForm());
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  /* ── View modal ─────────────────────────────────────────────── */
  const [viewModal,     setViewModal]     = useState({ open: false, capa: null });
  const [capaActions,   setCapaActions]   = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionForm,    setActionForm]    = useState(emptyActionForm());
  const [actionSaving,  setActionSaving]  = useState(false);
  const [actionError,   setActionError]   = useState('');

  /* ── Delete ─────────────────────────────────────────────────── */
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  /* ── Ref data ───────────────────────────────────────────────── */
  const [users, setUsers] = useState([]);

  /* ── Load list ──────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (debouncedSearch) params.search   = debouncedSearch;
      if (fStatus)         params.status   = fStatus;
      if (fSeverity)       params.severity = fSeverity;
      const { data: res } = await api.get('/capa', { params });
      setData(res);
    } catch { /* interceptor handles */ }
    finally { setLoading(false); }
  }, [page, debouncedSearch, fStatus, fSeverity]);

  useEffect(() => { load(); }, [load]);

  /* ── Load stats ─────────────────────────────────────────────── */
  useEffect(() => {
    api.get('/capa/stats').then(r => setStats(r.data.data || r.data)).catch(() => {});
  }, [data]);

  /* ── Load users for dropdowns ────────────────────────────────── */
  const loadUsers = async () => {
    try {
      const { data: res } = await api.get('/users', { params: { limit: 200 } });
      setUsers(res.data || []);
    } catch { setUsers([]); }
  };

  /* ── Load CAPA actions ───────────────────────────────────────── */
  const loadActions = async (capaId) => {
    setActionsLoading(true);
    try {
      const { data: res } = await api.get(`/capa/${capaId}/actions`);
      setCapaActions(res.data || []);
    } catch { setCapaActions([]); }
    finally { setActionsLoading(false); }
  };

  /* ── Open modals ─────────────────────────────────────────────── */
  const openAdd = async () => {
    await loadUsers();
    setForm(emptyForm());
    setError('');
    setModal({ open: true, mode: 'add', editId: null });
  };

  const openEdit = async (capa) => {
    await loadUsers();
    setForm({
      title:            capa.title || '',
      description:      capa.description || '',
      type:             capa.type || 'corrective',
      source:           capa.source || '',
      severity:         capa.severity || 'major',
      status:           capa.status || 'open',
      root_cause:       capa.root_cause || '',
      immediate_action: capa.immediate_action || '',
      related_batch:    capa.related_batch || '',
      assigned_to:      capa.assigned_to || '',
      due_date:         capa.due_date ? capa.due_date.split('T')[0] : '',
    });
    setError('');
    setModal({ open: true, mode: 'edit', editId: capa.id });
  };

  const openView = async (capa) => {
    setViewModal({ open: true, capa });
    setActionForm(emptyActionForm());
    setActionError('');
    await loadActions(capa.id);
    if (!users.length) await loadUsers();
  };

  /* ── Save CAPA ───────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        assigned_to: form.assigned_to || null,
        due_date:    form.due_date || null,
      };
      if (modal.mode === 'add') {
        await api.post('/capa', payload);
      } else {
        await api.put(`/capa/${modal.editId}`, payload);
      }
      setModal({ open: false, mode: 'add', editId: null });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  /* ── Delete CAPA ─────────────────────────────────────────────── */
  const handleDelete = async () => {
    try {
      await api.delete(`/capa/${deleteConfirm.id}`);
      setDeleteConfirm(null); load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete.');
    }
  };

  /* ── Add action ──────────────────────────────────────────────── */
  const handleAddAction = async () => {
    if (!actionForm.description.trim()) { setActionError('Description is required.'); return; }
    setActionSaving(true); setActionError('');
    try {
      await api.post(`/capa/${viewModal.capa.id}/actions`, {
        ...actionForm,
        assigned_to: actionForm.assigned_to || null,
        due_date:    actionForm.due_date || null,
      });
      setActionForm(emptyActionForm());
      await loadActions(viewModal.capa.id);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to add action.');
    } finally { setActionSaving(false); }
  };

  /* ── Toggle action status ────────────────────────────────────── */
  const toggleActionStatus = async (actionId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await api.put(`/capa/${viewModal.capa.id}/actions/${actionId}`, { status: newStatus });
      await loadActions(viewModal.capa.id);
    } catch { alert('Failed to update action status.'); }
  };

  /* ── Delete action ───────────────────────────────────────────── */
  const deleteAction = async (actionId) => {
    if (!window.confirm('Delete this action?')) return;
    try {
      await api.delete(`/capa/${viewModal.capa.id}/actions/${actionId}`);
      await loadActions(viewModal.capa.id);
    } catch { alert('Failed to delete action.'); }
  };

  const isAdmin = isPriv(user?.role);

  /* ── Stats bar ───────────────────────────────────────────────── */
  const statCards = stats ? [
    { label: 'Total CAPAs',  value: stats.total || 0,       color: '#6366f1' },
    { label: 'Open',         value: stats.open || 0,        color: '#3b82f6' },
    { label: 'In Progress',  value: stats.in_progress || 0, color: '#f59e0b' },
    { label: 'Overdue',      value: stats.overdue || 0,     color: '#ef4444' },
  ] : [];

  return (
    <div className="page">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">CAPA</h1>
          <p className="page-subtitle">Corrective & Preventive Action management</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={openAdd}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New CAPA
          </button>
        )}
      </div>

      {/* ── Stats bar ────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
          {statCards.map(c => (
            <div key={c.label} className="card" style={{ padding: '14px 16px', textAlign: 'center', borderTop: `3px solid ${c.color}` }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input className="input" placeholder="Search CAPA #, title, source…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: '1 1 200px', minWidth: 160 }} />

          <select className="input" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }} style={{ flex: '0 0 150px' }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
          </select>

          <select className="input" value={fSeverity} onChange={e => { setFSeverity(e.target.value); setPage(1); }} style={{ flex: '0 0 140px' }}>
            <option value="">All Severities</option>
            {SEVERITIES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>

          {(fStatus || fSeverity || search) && (
            <button className="btn btn-secondary" onClick={() => { setSearch(''); setFStatus(''); setFSeverity(''); setPage(1); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : data.data.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No CAPAs found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>CAPA #</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map(capa => (
                  <tr key={capa.id}>
                    <td>
                      <button className="btn-link" onClick={() => openView(capa)}
                        style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {capa.capa_number || `CAPA-${capa.id}`}
                      </button>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{capa.title}</div>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.78rem', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 10, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                        {typeLabels[capa.type] || capa.type}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{capa.source || '—'}</td>
                    <td><SeverityBadge severity={capa.severity} /></td>
                    <td><Badge color={statusColors[capa.status]}>{statusLabels[capa.status] || capa.status}</Badge></td>
                    <td style={{ fontSize: '0.82rem' }}>{capa.assigned_to_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>
                      <span style={{ fontSize: '0.82rem', color: isOverdue(capa.due_date, capa.status) ? '#ef4444' : 'inherit', fontWeight: isOverdue(capa.due_date, capa.status) ? 600 : 400 }}>
                        {fmt(capa.due_date)}
                        {isOverdue(capa.due_date, capa.status) && <span style={{ marginLeft: 4, fontSize: '0.68rem' }}>OVERDUE</span>}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openView(capa)}>View</button>
                        {isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => openEdit(capa)}>Edit</button>}
                        {isAdmin && (
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteConfirm(capa)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
      </div>

      {/* ── View Modal ───────────────────────────────────────────── */}
      {viewModal.open && viewModal.capa && (
        <Modal open={viewModal.open} size="lg"
          title={`${viewModal.capa.capa_number || `CAPA-${viewModal.capa.id}`} — ${viewModal.capa.title}`}
          onClose={() => setViewModal({ open: false, capa: null })}>
          <div style={{ display: 'grid', gap: 16 }}>
            {/* Badges row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Badge color={statusColors[viewModal.capa.status]}>{statusLabels[viewModal.capa.status]}</Badge>
              <SeverityBadge severity={viewModal.capa.severity} />
              <span style={{ fontSize: '0.78rem', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 10, color: 'var(--text-secondary)' }}>
                {typeLabels[viewModal.capa.type]}
              </span>
            </div>

            {/* Detail grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Source"      value={viewModal.capa.source} />
              <Field label="Assigned To" value={viewModal.capa.assigned_to_name} />
              <Field label="Due Date"    value={fmt(viewModal.capa.due_date)} />
              <Field label="Related Batch" value={viewModal.capa.related_batch} />
              <Field label="Created"     value={fmt(viewModal.capa.created_at)} />
              <Field label="Created By"  value={viewModal.capa.created_by_name} />
            </div>

            {viewModal.capa.description && (
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>DESCRIPTION</div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.88rem', background: 'var(--surface-2)', padding: '10px 14px', borderRadius: 8 }}>{viewModal.capa.description}</p>
              </div>
            )}

            {viewModal.capa.root_cause && (
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>ROOT CAUSE</div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.88rem' }}>{viewModal.capa.root_cause}</p>
              </div>
            )}

            {viewModal.capa.immediate_action && (
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>IMMEDIATE ACTION</div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.88rem' }}>{viewModal.capa.immediate_action}</p>
              </div>
            )}

            {/* Actions section */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: 12, color: 'var(--text-primary)' }}>
                CAPA Actions
                {capaActions.length > 0 && <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>{capaActions.filter(a => a.status === 'completed').length}/{capaActions.length} completed</span>}
              </div>

              {actionsLoading ? (
                <div style={{ textAlign: 'center', padding: 16 }}><div className="spinner" /></div>
              ) : capaActions.length === 0 ? (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', padding: '8px 0' }}>No actions added yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
                  {capaActions.map(action => (
                    <div key={action.id} style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 8,
                      opacity: action.status === 'completed' ? 0.75 : 1,
                    }}>
                      <button
                        onClick={() => toggleActionStatus(action.id, action.status)}
                        style={{
                          width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 2, cursor: 'pointer',
                          background: action.status === 'completed' ? '#10b981' : '#fff',
                          border: `2px solid ${action.status === 'completed' ? '#10b981' : 'var(--border-dark)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {action.status === 'completed' && (
                          <svg width="11" height="11" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </button>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, textDecoration: action.status === 'completed' ? 'line-through' : 'none' }}>{action.description}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 10 }}>
                          {action.assigned_to_name && <span>Assigned: {action.assigned_to_name}</span>}
                          {action.due_date && <span>Due: {fmt(action.due_date)}</span>}
                        </div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => deleteAction(action.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, flexShrink: 0 }}>
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add action form */}
              {isAdmin && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add Action</div>
                  {actionError && <div style={{ fontSize: '0.8rem', color: '#dc2626', marginBottom: 8 }}>{actionError}</div>}
                  <div style={{ display: 'grid', gap: 8 }}>
                    <textarea className="form-control" rows={2} placeholder="Action description…"
                      value={actionForm.description}
                      onChange={e => setActionForm(p => ({ ...p, description: e.target.value }))}
                      style={{ resize: 'vertical', fontSize: '0.85rem' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                      <div>
                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Assign To</label>
                        <select className="form-control" value={actionForm.assigned_to}
                          onChange={e => setActionForm(p => ({ ...p, assigned_to: e.target.value }))}
                          style={{ fontSize: '0.82rem' }}>
                          <option value="">— Anyone —</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Due Date</label>
                        <input className="form-control" type="date" value={actionForm.due_date}
                          onChange={e => setActionForm(p => ({ ...p, due_date: e.target.value }))}
                          style={{ fontSize: '0.82rem' }} />
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={handleAddAction} disabled={actionSaving}>
                        {actionSaving ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {isAdmin && (
                <button className="btn btn-secondary" onClick={() => { setViewModal({ open: false, capa: null }); openEdit(viewModal.capa); }}>Edit CAPA</button>
              )}
              <button className="btn btn-secondary" onClick={() => setViewModal({ open: false, capa: null })}>Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────────── */}
      {modal.open && (
        <Modal open={modal.open} size="lg"
          title={modal.mode === 'add' ? 'New CAPA' : 'Edit CAPA'}
          onClose={() => setModal({ open: false, mode: 'add', editId: null })}>
          <div style={{ display: 'grid', gap: 18 }}>
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-sm)', padding: '10px 14px', color: '#dc2626', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '16px 18px', display: 'grid', gap: 14 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Basic Info</div>

              <div>
                <label className="form-label">Title <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-control" value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Brief description of the issue" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Type</label>
                  <select className="form-control" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    {TYPES.map(t => <option key={t} value={t}>{typeLabels[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Severity</label>
                  <select className="form-control" value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))}>
                    {SEVERITIES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Source</label>
                  <input className="form-control" value={form.source}
                    onChange={e => setForm(p => ({ ...p, source: e.target.value }))}
                    placeholder="Audit, Customer complaint, Internal…" />
                </div>
                <div>
                  <label className="form-label">Related Batch</label>
                  <input className="form-control" value={form.related_batch}
                    onChange={e => setForm(p => ({ ...p, related_batch: e.target.value }))}
                    placeholder="Batch number (optional)" />
                </div>
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Detailed description of the non-conformance or issue…"
                  style={{ resize: 'vertical' }} />
              </div>
            </div>

            {/* Analysis */}
            <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '16px 18px', display: 'grid', gap: 14 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Analysis & Actions</div>

              <div>
                <label className="form-label">Root Cause</label>
                <textarea className="form-control" rows={2} value={form.root_cause}
                  onChange={e => setForm(p => ({ ...p, root_cause: e.target.value }))}
                  placeholder="Why did this issue occur?"
                  style={{ resize: 'vertical' }} />
              </div>

              <div>
                <label className="form-label">Immediate Action</label>
                <textarea className="form-control" rows={2} value={form.immediate_action}
                  onChange={e => setForm(p => ({ ...p, immediate_action: e.target.value }))}
                  placeholder="Actions taken immediately to contain the issue…"
                  style={{ resize: 'vertical' }} />
              </div>
            </div>

            {/* Assignment */}
            <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '16px 18px', display: 'grid', gap: 14 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Assignment</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Assigned To</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Due Date</label>
                  <input className="form-control" type="date" value={form.due_date}
                    onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
              <button className="btn btn-secondary" onClick={() => setModal({ open: false, mode: 'add', editId: null })}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ minWidth: 130 }}>
                {saving
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</span>
                  : modal.mode === 'add' ? 'Create CAPA' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete confirm ───────────────────────────────────────── */}
      {deleteConfirm && (
        <Modal open={!!deleteConfirm} title="Delete CAPA" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to delete <strong>{deleteConfirm.capa_number || `CAPA-${deleteConfirm.id}`}</strong> — "{deleteConfirm.title}"? This action cannot be undone.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Field helper ─────────────────────────────────────────────── */
function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: '0.88rem' }}>{value || '—'}</div>
    </div>
  );
}
